import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import UIWellBigNumber from 'UIComponents/well/UIWellBigNumber';
import UIWellItem from 'UIComponents/well/UIWellItem';
import UIWell from 'UIComponents/well/UIWell';
import UIWellLabel from 'UIComponents/well/UIWellLabel';
import styled from 'styled-components';
import { CALYPSO, OZ } from 'HubStyleTokens/colors';
import ColorCirclesHeader from '../../components/ColorCirclesHeader';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import { FormattedMessage } from 'LearningCenterUI/components/I18n'; // @ts-expect-error Could not find a declaration file for module

import useMediaQuery from '../../hooks/useMediaQuery';
import { device } from '../../constants/StyledComponentConstants';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import H6 from 'UIComponents/elements/headings/H6';
import { GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import WizardProgressSection from '../../components/Wizard/WizardProgressSection';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UsageTracker from '../../utils/UsageTracker';
import { HUBSPOT_SOFTWARE_INDEX, useCurrentLearningInterest, useShowWizardLanguageToggle } from '../../features/wizard';
import { useSelector } from 'react-redux';
import { WIZARD_URL_FRAGMENT } from '../../constants/Navigation';
import { useWizardParams } from '../../hooks/useWizard';
import { useLocation } from 'react-router-dom';
const BorderedUIWell = styled(UIWell).withConfig({
  displayName: "WizardProgressInfoPanel__BorderedUIWell",
  componentId: "sg1l0v-0"
})(["border-bottom:1px solid rgba(255,255,255,0.4);&.responsive{padding:10px 0 0;}"]);
const SingleUIWellItem = styled(UIWellItem).withConfig({
  displayName: "WizardProgressInfoPanel__SingleUIWellItem",
  componentId: "sg1l0v-1"
})(["&&{margin-top:0;margin-bottom:25px;}"]);
const ResultsColoredPanel = styled.div.withConfig({
  displayName: "WizardProgressInfoPanel__ResultsColoredPanel",
  componentId: "sg1l0v-2"
})(["background:linear-gradient(to right,", " 0%,", " 80%);width:340px;height:484px;overflow:hidden;margin-top:10px;padding-top:10px;transition:flex 0.2s ease-in-out;@media (min-width:", "){float:right;}@media (max-width:", "){width:100%;flex:0 0 ", ";z-index:100;border-top:1px solid white;margin-top:0;padding-top:0;&.expanded{flex:0 0 100%;}}"], CALYPSO, OZ, GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_SMALL, props => props.$showLanguageToggle ? '120px' : '60px');
const TransparentColorCirclesBG = styled(ColorCirclesHeader).withConfig({
  displayName: "WizardProgressInfoPanel__TransparentColorCirclesBG",
  componentId: "sg1l0v-3"
})(["background-color:transparent;padding:0;width:100%;height:100%;&::before{top:-110px;left:-90px;width:150%;height:150%;transform:rotate(45deg);}"]);
const InnerContainer = styled.div.withConfig({
  displayName: "WizardProgressInfoPanel__InnerContainer",
  componentId: "sg1l0v-4"
})(["padding:0 30px;@media (max-width:", "){padding:0 10px;}"], GRID_BREAKPOINT_SMALL);
const ResultCountContainer = styled(UIWellBigNumber).withConfig({
  displayName: "WizardProgressInfoPanel__ResultCountContainer",
  componentId: "sg1l0v-5"
})(["height:40px;overflow-y:hidden;margin-top:5px;font-size:48px;color:#fff;@media (max-width:", "){margin-top:0;height:46px;font-size:40px;}"], GRID_BREAKPOINT_SMALL);
const ExpandButton = styled(UIIconButton).withConfig({
  displayName: "WizardProgressInfoPanel__ExpandButton",
  componentId: "sg1l0v-6"
})(["transform:translateX(8px);"]);

const WizardProgressInfoPanel = ({
  resultsCount,
  isRequesting,
  onStepClick,
  onSetExpanded,
  languageToggle
}) => {
  const location = useLocation();
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  const {
    selectedCategory,
    selectedCategoryGroup,
    selectedTool,
    selectedToolsGroup,
    isToolsSelected: hubspotToolSelected
  } = useCurrentLearningInterest();
  const {
    isProgressPanelExpanded: expanded
  } = useSelector(state => state.wizard);
  const {
    showOnlyCerts
  } = useWizardParams();
  const category = hubspotToolSelected ? selectedTool : selectedCategory;
  const categoryGroup = hubspotToolSelected ? selectedToolsGroup : selectedCategoryGroup;
  const {
    show: showLanguageToggle
  } = useShowWizardLanguageToggle();

  const getArrowIcon = () => {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: expanded ? 'up' : 'down',
      size: "medium",
      style: {
        color: 'white'
      }
    });
  };

  const getMotivationTextLine1 = () => {
    return showOnlyCerts ? 'learningCenter.wizard.modal.motivationbuttons.advancecareer' : 'learningCenter.wizard.modal.motivationbuttons.currenttrends';
  };

  const renderHeaderTitle = () => {
    return /*#__PURE__*/_jsx(UIWellLabel, {
      style: {
        color: 'white'
      },
      children: /*#__PURE__*/_jsx(H6, {
        className: "m-bottom-0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.wizard.progresspanel.courses"
        })
      })
    });
  };

  const renderCount = () => {
    return isRequesting ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      size: 'sm'
    }) : I18n.formatNumber(resultsCount);
  };

  const getHeader = () => {
    return /*#__PURE__*/_jsx(BorderedUIWell, {
      className: "m-top-2",
      children: /*#__PURE__*/_jsxs(SingleUIWellItem, {
        children: [renderHeaderTitle(), /*#__PURE__*/_jsx(ResultCountContainer, {
          "data-test-id": "wizard-results-count",
          children: renderCount()
        }), languageToggle]
      })
    });
  };

  const onClickExpanded = () => {
    UsageTracker.track('wizardInteraction', {
      action: 'wizard-expand-results-section-mobile'
    });
    onSetExpanded(!expanded);
  };

  const onSectionClick = step => {
    UsageTracker.track('wizardInteraction', {
      action: 'wizard-click-green-results-section',
      stepClicked: step
    });
    onStepClick();
  };

  const getMobileHeader = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(BorderedUIWell, {
        className: "responsive",
        children: /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(UIBox, {
            className: "m-right-2",
            children: /*#__PURE__*/_jsx(ResultCountContainer, {
              children: renderCount()
            })
          }), /*#__PURE__*/_jsx(UIBox, {
            grow: 1,
            children: renderHeaderTitle()
          }), /*#__PURE__*/_jsx(UIBox, {
            children: /*#__PURE__*/_jsx(ExpandButton, {
              "data-test-id": "wizard-progress-header-expand",
              onClick: onClickExpanded,
              use: "transparent",
              children: getArrowIcon()
            })
          })]
        })
      }), /*#__PURE__*/_jsx(UIBox, {
        className: "m-top-3 m-bottom-3",
        children: languageToggle
      })]
    });
  };

  const getProgressPanelHeader = () => {
    return isMobile ? getMobileHeader() : getHeader();
  };

  const getSectionUrl = step => {
    let url = WIZARD_URL_FRAGMENT;
    if (hubspotToolSelected) url += `/${HUBSPOT_SOFTWARE_INDEX}`;
    const {
      search
    } = location;

    switch (step) {
      case 'motivation':
        return `${[url, categoryGroup].filter(e => e).join('/')}${search}`;

      case 'final':
        return `${[url, categoryGroup, category].filter(e => e).join('/')}${search}`;

      default:
        return `${url}${search}`;
    }
  };

  const renderSectionStepCategory = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-1",
      link: getSectionUrl('category'),
      linkMessageLine1: `learningCenter.filtersGroups.${categoryGroup}`,
      noteMessage: "learningCenter.wizard.progresspanel.categorygrouptitle",
      onProgressStepClick: () => {
        onSectionClick('category');
      }
    });
  };

  const renderSectionStepMotivation = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-2",
      link: getSectionUrl('motivation'),
      linkMessageLine1: hubspotToolSelected ? `learningCenter.topFilters.tools.${category}` : `learningCenter.topFilters.cat.${category}`,
      noteMessage: "learningCenter.wizard.progresspanel.categorytitle",
      noteOptions: {
        categoryGroup: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `learningCenter.filtersGroups.${categoryGroup}`
        })
      },
      onProgressStepClick: () => {
        onSectionClick('motivation');
      }
    });
  };

  const renderSectionStepFinal = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-3",
      link: getSectionUrl('final'),
      linkMessageLine1: getMotivationTextLine1(),
      noteMessage: "learningCenter.wizard.progresspanel.motivationtitle",
      onProgressStepClick: () => {
        onSectionClick('final');
      }
    });
  };

  return /*#__PURE__*/_jsx(ResultsColoredPanel, {
    $showLanguageToggle: showLanguageToggle,
    className: expanded ? 'expanded' : '',
    children: /*#__PURE__*/_jsx(TransparentColorCirclesBG, {
      children: /*#__PURE__*/_jsxs(InnerContainer, {
        children: [/*#__PURE__*/_jsx(UIFlex, {
          direction: "column"
        }), getProgressPanelHeader(), categoryGroup && renderSectionStepCategory(), category && renderSectionStepMotivation(), showOnlyCerts !== undefined && renderSectionStepFinal()]
      })
    })
  });
};

export default WizardProgressInfoPanel;